import type * as React from 'react';
import { DRAFT } from '../../constants/draft.constants';
import { SimpleDialog } from '../UI/SimpleDialog';

export interface DeleteDraftConfirmProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * TRIL-571 — the confirm shown before a draft is deleted.
 *
 * Shared by PostDraft and PostReplyDraft so the copy and the behaviour cannot drift
 * between the two surfaces, and — the reason it is a component rather than two inline
 * dialogs — so the guard is testable at all. Both host components are too heavy to mount
 * in jsdom (PostDraft OOMs; see PostDraft.publishHint.test.tsx), which is exactly how a
 * confirm dialog could be removed and nothing would go red.
 *
 * Deleting is irreversible and cascades well past the row: the DDB draft, the S3 content,
 * every textBlocksVersions object, the stored analysis, and (on PostDraft) embedded images.
 *
 * `data` is deliberately NOT passed to SimpleDialog. It hides its action button when
 * `data.hasStoryAnalysis` is truthy — which here would remove the Delete button from the
 * confirm for precisely the drafts with the most to lose.
 */
export const DeleteDraftConfirm = ({
  open,
  onCancel,
  onConfirm,
}: DeleteDraftConfirmProps): React.ReactElement => (
  <SimpleDialog
    className={'DELETE__DRAFT__MODAL'}
    open={open}
    onClose={onCancel}
    onSubmit={onConfirm}
    dialogTitle={DRAFT.DELETE_DRAFT_DIALOG.TITLE}
    dialogBody={DRAFT.DELETE_DRAFT_DIALOG.BODY}
    closeModalLabel={DRAFT.DELETE_DRAFT_DIALOG.CANCEL}
    actionLabel={DRAFT.DELETE_DRAFT_DIALOG.DELETE}
  />
);

export default DeleteDraftConfirm;
